import request from '@/umi-request';
import { AxiosPromise } from 'axios';

import {
  Account,
  AccountDeposit,
  AccountIncomingTransfer,
  AccountOutgoingTransfer,
  AccountPayment,
  AccountWithdraw,
  Check,
  FilePayment,
  ICreatePartialPaymentRequestPayload,
  IPartialPaymentRequest,
  Liquidation,
  LiquidationDefaults,
  LiquidationThirdParty,
  LiquidationThirdPartyChanges,
  LiquidationTransaction,
  SimpleFileAttachment,
} from '@/interfaces/files';
import { PaginatedResponse } from '@/interfaces/response';

export async function getPayments(params: any = {}) {
  return request({
    url: `payments/?all=true`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getPendingPayments(params: any = {}) {
  return request({
    url: `payments/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getFilePaymentHistory(id: number) {
  return request({
    url: `payments/${id}/changes-history/`,
    method: 'GET',
  }) as AxiosPromise<FilePayment[]>;
}

export async function verifyPayment(id: number, data: any) {
  return request({
    url: `payments/${id}/verify/`,
    method: 'PUT',
    data
  }) as AxiosPromise<FilePayment>;
}

export async function cancelPayment(id: number) {
  return request({
    url: `payments/${id}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<FilePayment>;
}

export async function payementComplete(id: number) {
  return request({
    url: `payments/${id}/pay/`,
    method: 'PUT',
  }) as AxiosPromise<FilePayment>;
}

export async function getLiquidationDefaults() {
  return request({
    url: `file-liquidations/defaults/`,
    method: 'GET',
  }) as AxiosPromise<LiquidationDefaults>;
}

export async function generateLiquidation(data: any) {
  return request({
    url: `file-liquidations/`,
    method: 'POST',
    data
  }) as AxiosPromise<Liquidation>;
}

// *********************** //
// Third Party Liquidations
// *********************** //
export async function generateThirdPartyLiquidation(data: any) {
  return request({
    url: `third-party-liquidations/`,
    method: 'POST',
    data
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function getThirdPartyLiquidations(params: any = {}) {
  return request({
    url: `third-party-liquidations/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<LiquidationThirdParty>>;
}

export async function getThirdPartyLiquidation(id: number) {
  return request({
    url: `third-party-liquidations/${id}/`,
    method: 'GET',
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function downloadThirdPartyLiquidationPayment(liquidation: number) {
  return request({
    url: `third-party-liquidations/${liquidation}/download_template/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function getThirdPartyLiquidationChanges(id: number) {
  return request({
    url: `third-party-liquidations/${id}/changes-history/`,
    method: 'GET',
  }) as AxiosPromise<LiquidationThirdPartyChanges[]>;
}

export async function getThirdPartyLiquidationPayments(id: number, params: any = {}) {
  return request({
    url: `third-party-liquidations/${id}/get-payments/`,
    method: 'GET',
    params,
  }) as AxiosPromise<FilePayment[]>;
}

export async function addThirdPartyLiquidationPayments(id: number, data: any) {
  return request({
    url: `third-party-liquidations/${id}/add-payments/`,
    method: 'POST',
    data
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function removeThirdPartyLiquidationPayment(id: number, data: any) {
  return request({
    url: `third-party-liquidations/${id}/remove-payment/`,
    method: 'POST',
    data
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function uploadPrePaymentAttachment(data: any) {
  return request({
    url: `third-party-liquidations/upload-attachment/`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data
  }) as AxiosPromise<SimpleFileAttachment>;
}

export async function uploadSimpleFileAttachment(id: number, data: any) {
  return request({
    url: `third-party-liquidations/${id}/upload-simple-attachment/`,
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function removeSimpleFileAttachment(id: number, data: any) {
  return request({
    url: `third-party-liquidations/${id}/delete-simple-attachment/`,
    method: 'POST',
    data
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function verifyThirdPartyLiquidation(id: number) {
  return request({
    url: `third-party-liquidations/${id}/verify/`,
    method: 'PUT',
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function deleteThirdPartyLiquidation(id: number) {
  return request({
    url: `third-party-liquidations/${id}/`,
    method: 'DELETE',
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function completeThirdPartyLiquidation(id: number, data: any) {
  return request({
    url: `third-party-liquidations/${id}/execute/`,
    method: 'PUT',
    data
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function updateOrderNumber(id: number, number: number) {
  return request({
    url: `third-party-liquidations/${id}/update-order-number/`,
    method: 'PUT',
    data: { number }
  }) as AxiosPromise<LiquidationThirdParty>;
}

export async function getLiquidations(params: any = {}) {
  return request({
    url: `file-liquidations/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<Liquidation>>;
}

export async function getLiquidation(id: number) {
  return request({
    url: `file-liquidations/${id}/`,
    method: 'GET',
  }) as AxiosPromise<Liquidation>;
}

export async function getLiquidationChanges(id: number) {
  return request({
    url: `file-liquidations/${id}/changes-history/`,
    method: 'GET',
  }) as AxiosPromise<Liquidation[]>;
}
export async function getChashPayments(params: any = {}) {
  return request({
    url: `cash-payments/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountPayment>>;
}

export async function getLiquidationFile(id: number) {
  return request({
    url: `file-liquidations/${id}/export-file/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function getLiquidationPDF(id: number) {
  return request({
    url: `file-liquidations/${id}/export-pdf/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function generateCheckReceipt(id: number) {
  return request({
    url: `checks/${id}/receipt/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}


export async function generateCashPaymentReceipt(id: number) {
  return request({
    url: `cash-payments/${id}/receipt/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}
export async function getLiquidationXLSX(id: number) {
  return request({
    url: `file-liquidations/${id}/export-xlsx/`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function getPendingPaymentsXLSX(params: any = {}, id: number = 1) {
  return request({
    url: `payments/${id}/pre_liquidation_xlsx`,
    method: 'GET',
    responseType: 'blob',
    params,
  }) as AxiosPromise<Blob>;
}

export async function getPendingLiquidationXLSX(params: any = {}) {
  return request({
    url: `payments/pending_payments_xlsx/`,
    method: 'GET',
    responseType: 'blob',
    params,
  }) as AxiosPromise<Blob>;
}

export async function getPendingPaymentsPDF(id: number = 1) {
  return request({
    url: `payments/${id}/pre_liquidation_pdf`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getPendingLiquidationPDF(params: any = {}) {
  return request({
    url: `payments/pending_payments_pdf/`,
    method: 'GET',
    params,
    responseType: 'blob',
  }) as AxiosPromise<Blob>;
}

export async function getPaymentsPDF(data: any) {
  return request({
    url: `payments/liquidations_pdf/`,
    method: 'POST',
    responseType: 'blob',
    data
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getFilePaymentsPDF(id: number) {
  return request({
    url: `payments/${id}/file_liquidations_pdf`,
    method: 'GET',
    responseType: 'blob',
  }) as AxiosPromise<PaginatedResponse<FilePayment>>;
}

export async function getLiquidationTransations(id: number, params: any = {}) {
  return request({
    url: `file-liquidations/${id}/transactions/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<LiquidationTransaction>>;
}

export async function completeLiquidation(id: number, data: any) {
  return request({
    url: `file-liquidations/${id}/execute/`,
    method: 'PUT',
    data,
    timeout: 180000
  }) as AxiosPromise<LiquidationTransaction>;
}

export async function verifyLiquidation(id: number) {
  return request({
    url: `file-liquidations/${id}/verify/`,
    method: 'PUT',
  }) as AxiosPromise<LiquidationTransaction>;
}

export async function cancelLiquidation(id: number) {
  return request({
    url: `file-liquidations/${id}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<LiquidationTransaction>;
}

export async function markErrorForLiquidationTransaction(
  liquidation_id: number,
  transaction_id: number,
) {
  return request({
    url: `file-liquidations/${liquidation_id}/transactions/${transaction_id}/error/`,
    method: 'PUT',
  }) as AxiosPromise<LiquidationTransaction>;
}

export async function getChecks(params: any = {}) {
  return request({
    url: `checks/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<Check>>;
}

export async function generateCheck(data: any) {
  return request({
    url: `checks/`,
    method: 'POST',
    data
  }) as AxiosPromise<Check>;
}

export async function cancelCheck(id: number) {
  return request({
    url: `checks/${id}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<Check>;
}

export async function paidCheck(id: number) {
  return request({
    url: `checks/${id}/paidcheck/`,
    method: 'PUT',
  }) as AxiosPromise<Check>;
}

export async function getAccounts(params: any = {}) {
  return request({
    url: `accounts/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<Account>>;
}

export async function getAccount(id: number) {
  return request({
    url: `accounts/${id}/`,
    method: 'GET',
  }) as AxiosPromise<Account>;
}

export async function getAccountPayments(id: number, params: any = {}) {
  return request({
    url: `accounts/${id}/payments/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountPayment>>;
}

export async function getAccountCollections(id: number, params: any = {}) {
  return request({
    url: `accounts/${id}/collections/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountPayment>>;
}

export async function cancelAccountCollection(id: number, paymentId: number) {
  return request({
    url: `accounts/${id}/collections/${paymentId}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<Check>;
}

export async function createAccountPayment(id: number, data: any) {
  return request({
    url: `accounts/${id}/payments/`,
    method: 'POST',
    data
  }) as AxiosPromise<Check>;
}

export async function cancelAccountPayment(id: number, paymentId: number) {
  return request({
    url: `accounts/${id}/payments/${paymentId}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<Check>;
}

export async function getAccountDeposits(id: number, params: any = {}) {
  return request({
    url: `accounts/${id}/deposits/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountDeposit>>;
}

export async function getAccountIncomingTransfers(id: number, params: any = {}) {
  return request({
    url: `accounts/${id}/incoming-transfers/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountIncomingTransfer>>;
}

export async function getAccountOutgoingTransfers(id: number, params: any = {}) {
  return request({
    url: `accounts/${id}/outgoing-transfers/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountOutgoingTransfer>>;
}

export async function getAccountWithdraws(id: number, params: any = {}) {
  return request({
    url: `accounts/${id}/withdrawals/`,
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<AccountWithdraw>>;
}

export async function createAccountDeposit(id: number, data: any) {
  return request({
    url: `accounts/${id}/deposits/`,
    method: 'POST',
    data
  }) as AxiosPromise<AccountDeposit>
}

export async function createAccountWithdraw(id: number, data: any) {
  return request({
    url: `accounts/${id}/withdrawals/`,
    method: 'POST',
    data
  }) as AxiosPromise<AccountWithdraw>;
}

export async function createAccountTransfer(id: number, data: any) {
  return request({
    url: `accounts/${id}/outgoing-transfers/`,
    method: 'POST',
    data
  }) as AxiosPromise<AccountOutgoingTransfer>;
}

export async function cancelAccountDeposit(id: number, deposit_id: number) {
  return request({
    url: `accounts/${id}/deposits/${deposit_id}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<AccountDeposit>;
}

export async function cancelAccountWithdraw(id: number, withdraw_id: number) {
  return request({
    url: `accounts/${id}/withdrawals/${withdraw_id}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<AccountWithdraw>;
}

export async function cancelAccountTransfer(id: number, transfer_id: number) {
  return request({
    url: `accounts/${id}/outgoing-transfers/${transfer_id}/cancel/`,
    method: 'PUT',
  }) as AxiosPromise<AccountOutgoingTransfer>;
}

export async function getAllPartialPaymentsRequest(params: any = {}) {
  return request({
    url: '/partial-payment-requests/',
    method: 'GET',
    params,
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export async function getPartialPaymentRequest(id: number | string) {
  return request({
    url: `/partial-payment-requests/${id}/`,
    method: 'GET',
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export async function createPartialPaymentRequest(
  payload: ICreatePartialPaymentRequestPayload,
) {
  return request({
    url: `/partial-payment-requests/`,
    method: 'POST',
    data: payload,
  }) as AxiosPromise<IPartialPaymentRequest>;
}

export async function transferPartialPaymentRequest(
  id: number | string,
  destination: number
) {
  return request({
    url: `/partial-payment-requests/${id}/transfer/`,
    method: 'PUT',
    data: {
      destination,
    },
  }) as AxiosPromise<IPartialPaymentRequest>;
}

export async function preApprovedPartialPaymentRequest(id: number | string) {
  return request({
    url: `/partial-payment-requests/${id}/pre-approve/`,
    method: 'PUT',
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export async function approvedPartialPaymentRequest(id: number | string) {
  return request({
    url: `/partial-payment-requests/${id}/approve/`,
    method: 'PUT',
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export async function rejectedPartialPaymentRequest(id: number | string, observation?: string) {
  return request({
    url: `/partial-payment-requests/${id}/reject/`,
    method: 'PUT',
    data: {
      observation,
    }
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export function formatEditPartialPaymentRequestPayload(data: any) {
  return {
    observation: data.observation,
    coverages: (data.coverages || []).map((coverage: any) => ({
      coverage_reference_id: Number(coverage.coverage_reference_id ?? coverage.coverage_id),
      quantity: Number(coverage.quantity || 0),
      amount: Number(coverage.amount || 0),
    })),
  };
}

export async function editPartialPaymentRequest(id: number | string, data: any) {
  return request({
    url: `/partial-payment-requests/${id}/edit/`,
    method: 'PUT',
    data,
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export async function editTicketToPartialPaymentRequest(request_id: number | string, ticket_id: number | string, data: any) {
  return request({
    url: `/partial-payment-requests/${request_id}/ticket-attachments/${ticket_id}/`,
    method: 'PUT',
    data,
  }) as AxiosPromise<PaginatedResponse<IPartialPaymentRequest>>;
}

export async function editReceiptToPartialPaymentRequest(request_id: number | string, receipt_id: number | string, data:any) {
  return request({
    url: `/partial-payment-requests/${request_id}/receipt-attachments/${receipt_id}/`,
    method: 'PUT',
    data,
  })
}

export async function editVoucherToPartialPaymentRequest(request_id: number | string, voucher_id: number | string, data:any) {
  return request({
    url: `/partial-payment-requests/${request_id}/voucher-attachments/${voucher_id}/`,
    method: 'PUT',
    data,
  })
}